/**
 * Helpers for surfacing offline / network-error feedback when a Save action
 * fails. Extracted so the timing-sensitive checks can be unit-tested without
 * mounting the host Vue component (HERA-7520).
 */

export const OFFLINE_SAVE_MESSAGE =
  'You appear to be offline. Your changes were not saved. Please reconnect and try again.';

/**
 * True when the browser reports it is offline. Returns false when
 * `navigator` is not available (e.g. SSR / unit test default), matching
 * the pre-existing optimistic behaviour.
 */
export function isOffline() {
  if (typeof navigator === 'undefined') return false;
  return navigator.onLine === false;
}

/**
 * Detect a network-related rejection coming from Amplify / axios / the
 * connectionHelper retry shim. We treat all of these shapes as "offline":
 *   - Error with message containing "Network" / "offline" / "Failed to fetch"
 *   - Amplify-style { errors: [{ message: 'Network Error' }] }
 *   - String error
 */
export function isNetworkError(e) {
  if (!e) return false;
  const NETWORK_RE = /Network Error|Failed to fetch|offline|ERR_INTERNET_DISCONNECTED/i;
  if (typeof e === 'string') return NETWORK_RE.test(e);
  if (typeof e.message === 'string' && NETWORK_RE.test(e.message)) return true;
  if (Array.isArray(e.errors) && e.errors.some(inner => inner && typeof inner.message === 'string' && NETWORK_RE.test(inner.message))) {
    return true;
  }
  return false;
}

/**
 * Pick the user-facing message for a save failure. If the user is offline
 * or the rejection looks like a network error, return the friendly offline
 * copy; otherwise return the original error so existing displayUserError()
 * formatting still applies.
 */
export function describeSaveError(e) {
  if (isOffline() || isNetworkError(e)) return OFFLINE_SAVE_MESSAGE;
  return e;
}
